"use client";

import { useId, useState } from "react";
import { trackFaqOpen } from "@/lib/metrics";

interface QAItemProps {
  className?: string;
  /** Question text */
  question: string;
  /** Answer text */
  answer: string;
  /** Controlled open state (optional) */
  open?: boolean;
  /** Toggle callback */
  onToggle?: () => void;
  /** Initial uncontrolled state. */
  defaultOpen?: boolean;
}

function ChevronDown() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** FAQ accordion item for «Частые вопросы» section */
export function QAItem({ className = "", question, answer, open: controlledOpen, onToggle, defaultOpen = false }: QAItemProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledOpen ?? internalOpen;
  const answerId = useId();

  const handleToggle = () => {
    if (!isOpen) trackFaqOpen(question);
    if (onToggle) onToggle();
    else setInternalOpen(!isOpen);
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-expanded={isOpen}
      aria-controls={answerId}
      className={`bg-white cursor-pointer flex flex-col gap-[16px] items-start p-[20px] rounded-[16px] w-full text-left transition-shadow hover:shadow-[0px_2px_8px_rgba(0,0,0,0.06)] ${className}`}
    >
      {/* Question row */}
      <div className="flex items-center justify-between w-full">
        <span
          className="flex-1 [font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-text min-w-0"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {question}
        </span>
        <span className={`shrink-0 text-text-subtle transition-transform ${isOpen ? "rotate-180" : ""}`}>
          <ChevronDown />
        </span>
      </div>

      {/* Answer */}
      {isOpen && (
        <p
          id={answerId}
          className="whitespace-pre-line [font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text-secondary w-full"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {answer}
        </p>
      )}
    </button>
  );
}
